import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { BG_LAYER, kitImage, loadImageSrc, loadKitFonts, renderDesign, type BrandKit, type Crop, type Lang, type Template } from '@/lib/brand-kit';

/** One static, drawn exactly as it exports. `images` maps a box id to a kit path or a
 *  blob: URL. The canvas is the full-size design; CSS shrinks it to the column. */
export default function DesignCanvas({ clientId, kit, template, lang, texts, images, crops, canvasRef, className, onReady, onSizes, overlay }: {
  clientId: string; kit: BrandKit; template: Template; lang: Lang;
  texts: Record<string, string>; images: Record<string, string>; crops?: Record<string, Crop>;
  canvasRef?: RefObject<HTMLCanvasElement>; className?: string; onReady?: (ok: boolean) => void;
  /** the natural size of each drawn photo (box id -> w, h), for the in-frame drag */
  onSizes?: (s: Record<string, { w: number; h: number }>) => void;
  /** laid exactly over the canvas (clickable photo frames) */
  overlay?: ReactNode;
}) {
  const own = useRef<HTMLCanvasElement>(null);
  const ref = canvasRef ?? own;
  const [error, setError] = useState('');
  const key = JSON.stringify([template, lang, texts, images, crops, kit.colours, kit.type.fonts, kit.logos]);

  useEffect(() => {
    let live = true;
    const load = (src: string) => (src.startsWith('blob:') || src.startsWith('data:') ? loadImageSrc(src) : kitImage(clientId, src)).catch(() => null);
    (async () => {
      await loadKitFonts(clientId, kit);
      const background = template.background ? await load(template.background) : null;
      const imgs: Record<string, HTMLImageElement | null> = {};
      await Promise.all(Object.entries(images).map(async ([id, src]) => { imgs[id] = src ? await load(src) : null; }));
      if (!live || !ref.current) return;
      renderDesign(ref.current, { kit, template, lang, texts, images: imgs, background, crops });
      setError('');
      onReady?.(true);
      // the background photo reports under BG_LAYER, so the editor can move and zoom it like a frame
      const drawn: [string, HTMLImageElement | null][] = [...Object.entries(imgs), [BG_LAYER, background]];
      onSizes?.(Object.fromEntries(drawn.filter(([, i]) => i).map(([id, i]) => [id, { w: i!.naturalWidth, h: i!.naturalHeight }])));
    })().catch((e) => { if (live) { setError(String(e?.message || e)); onReady?.(false); } });
    return () => { live = false; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={className}>
      <div className="relative">
        <canvas ref={ref} width={template.w} height={template.h} className="w-full h-auto block rounded-md shadow-lg bg-white/5"
          data-testid="design-canvas" />
        {overlay}
      </div>
      {error && <p className="text-xs text-red-400 mt-1">Could not draw: {error}</p>}
    </div>
  );
}
